// Pure helpers for urgency + filter / search predicates aligned with the
// backend Deal response shape: `expirationDate` is ISO 8601, `lifecycle.status`
// drives the canceled flag, and `invoiceSummary.invoiceStatuses` replaces the
// previously embedded `invoices[]` array.

import type { Deal, Urgency } from '../types/deal'

// DEMO_TODAY is kept as a test fixture so unit tests can pin urgency
// calculations to a fixed reference date. Production callers SHALL NOT use
// this — `getUrgency` and `dealMatchesStatus` default `today` to `new Date()`.
export const DEMO_TODAY = new Date(2027, 0, 5)

export type StatusFilter =
  | 'all'
  | 'healthy'
  | 'soon'
  | 'expired'
  | 'drafts'
  | 'canceled'
  | 'archived'

export type PaymentFilter = 'all' | 'paid' | 'partial' | 'unpaid' | 'scheduled'

export interface UrgencyInfo {
  tier: Urgency | 'pending'
  days: number | null
  label: string
}

export function getUrgency(
  expirationISO: string | null | undefined,
  today: Date = new Date(),
): UrgencyInfo {
  if (!expirationISO || typeof expirationISO !== 'string') {
    return { tier: 'pending', days: null, label: 'Awaiting activation' }
  }
  const expiration = new Date(expirationISO)
  if (Number.isNaN(expiration.getTime())) {
    return { tier: 'pending', days: null, label: 'Awaiting activation' }
  }
  const days = Math.round((expiration.getTime() - today.getTime()) / 86_400_000)
  if (days < 0) return { tier: 'expired', days, label: `Expired ${Math.abs(days)}d ago` }
  if (days <= 30) {
    return { tier: 'critical', days, label: days === 0 ? 'Expires today' : `Expires in ${days}d` }
  }
  if (days <= 90) return { tier: 'soon', days, label: `Expires in ${days}d` }
  return { tier: 'healthy', days, label: `${Math.round(days / 30)} mo left` }
}

export function isDealArchived(deal: Deal): boolean {
  return Boolean(deal.archivedAt)
}

export function isDealCanceled(deal: Deal): boolean {
  return deal.lifecycle?.status === 'canceled'
}

export function dealInvoiceStatuses(deal: Deal): string[] {
  return deal.invoiceSummary?.invoiceStatuses ?? []
}

export function dealMatchesPayment(deal: Deal, filter: PaymentFilter): boolean {
  if (filter === 'all') return true
  const statuses = dealInvoiceStatuses(deal)
  if (filter === 'paid') {
    return statuses.every((status) => status === 'Paid' || status === 'Voided') &&
      statuses.some((status) => status === 'Paid')
  }
  if (filter === 'partial') return statuses.some((status) => status === 'Partially Paid')
  if (filter === 'unpaid') return statuses.some((status) => status === 'Unpaid')
  if (filter === 'scheduled') return statuses.some((status) => status === 'Scheduled')
  return true
}

export function dealMatchesStatus(deal: Deal, filter: StatusFilter, today: Date = new Date()): boolean {
  const archived = isDealArchived(deal)
  const canceled = isDealCanceled(deal)
  if (filter === 'archived') return archived
  if (filter === 'canceled') return canceled && !archived
  if (archived || canceled) return false
  if (filter === 'all') return true
  if (filter === 'drafts') return dealInvoiceStatuses(deal).includes('Draft')
  const tier = getUrgency(deal.expirationDate, today).tier
  if (filter === 'expired') return tier === 'expired'
  if (filter === 'soon') return tier === 'soon' || tier === 'critical'
  if (filter === 'healthy') return tier === 'healthy'
  return true
}

export type CompanyNameLookup = (companyId: string) => string | undefined

export function dealMatchesSearch(
  deal: Deal,
  query: string,
  getCompanyName?: CompanyNameLookup,
): boolean {
  if (!query) return true
  const haystack = [
    getCompanyName?.(deal.companyId) ?? deal.companyId,
    deal.orderNumber,
    deal.quoteNumber,
    deal.salesRepInitials ?? '',
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(query.toLowerCase())
}

export interface ShipmentProgressDisplay {
  totalShip: number
  deliveredShip: number
  totalDevices: number
  deliveredDevices: number
  hasShipments: boolean
}

export function getShipmentProgress(deal: Deal): ShipmentProgressDisplay {
  const progress = deal.shipmentProgress
  if (!progress || !progress.hasShipments) {
    return {
      totalShip: 0,
      deliveredShip: 0,
      totalDevices: 0,
      deliveredDevices: 0,
      hasShipments: false,
    }
  }
  return {
    totalShip: progress.totalShip,
    deliveredShip: progress.deliveredShip,
    totalDevices: progress.totalDevices,
    deliveredDevices: progress.deliveredDevices,
    hasShipments: progress.hasShipments,
  }
}

// ISO 8601 → MM/DD/YYYY display string. Empty / nullish / invalid input
// returns ''. Tolerates full datetime strings (slices the date portion).
export function formatActivationLabel(iso: string | null | undefined): string {
  if (!iso) return ''
  const datePart = iso.length >= 10 ? iso.slice(0, 10) : iso
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart)
  if (!match) return ''
  const [, year, month, day] = match
  return `${month}/${day}/${year}`
}
